import { Chain, ChainSymbol } from "chainConfigs";
import { MIN_ENTRY_USD, MIN_ENTRY_USD_BUFFER } from "consts";

export type Mode = "BUY" | "SELL" | "SWAP" | "UNWRAP";
export const MODES: Record<Mode, Mode> = {
  BUY: "BUY",
  SELL: "SELL",
  SWAP: "SWAP",
  UNWRAP: "UNWRAP",
};

export interface TradingModeConfig {
  modeSwitch: Mode[] | null;
  supportedNativeChains: Partial<Record<Chain, Chain[]>>;
  minimumEntryUSD?: number;
  minimumGasBufferUSD?: Partial<Record<Chain, number>>;
  minimumCrossChainGasBufferUSD?: Partial<Record<Chain, number>>;
  minimumGasBufferAmount?: Partial<Record<Chain, number>>;
  minimumCrossChainGasBufferAmount?: Partial<Record<Chain, number>>;
  from: {
    isWrapped?: boolean;
    isNative: boolean;
    isBlockedSameChain?: boolean;
  };
  to: {
    isWrapped?: boolean;
    isNative: boolean;
    isBlockedSameChain?: boolean;
  };
  ctaColor: "green" | "red" | "primary";
  approvalSpendingAmount: Record<Chain, number>;
}

export const minimumGasBufferUSD: Partial<Record<Chain, number>> = {
  berachain: 0,
  ethereum: 0,
  solana: 0,
  base: 0,
  bsc: 0,
};

export const minimumCrossChainGasBufferUSD: Partial<Record<Chain, number>> = {
  berachain: 0,
  ethereum: 0,
  solana: 0,
  base: 0,
  bsc: 0,
};

export const minimumGasBufferAmount: Partial<Record<Chain, number>> = {
  berachain: 0.14, // 0.5USD
  ethereum: 0.00056, // 1.0USD
  solana: 0.00331, // 0.5USD
  base: 0.00028, // 0.5USD
  bsc: 0.0016, // 0.5USD
};

export const minimumCrossChainGasBufferAmount: Partial<Record<Chain, number>> = {
  berachain: 0.14, // 0.5USD
  ethereum: 0.00056, // 1.0USD
  solana: 0.00331, // 0.5USD
  base: 0.00028, // 0.5USD
  bsc: 0.0016, // 0.5USD
};

export const approvalSpendingAmount: Record<Chain, number> = {
  berachain: 999999999999, // 0.5USD
  ethereum: 999999999999, // 1.0USD
  solana: 999999999999, // 0.5USD
  base: 999999999999, // 0.5USD
  bsc: 999999999999, // 0.5USD
};

export const UNSUPPORTED_SWAP_TOKENS: Array<ChainSymbol> = ["TON"];
export const TRADING_MODE_CONFIG_MAP: Record<Mode, TradingModeConfig> = {
  BUY: {
    modeSwitch: ["SELL", "BUY"],
    supportedNativeChains: {
      berachain: ["solana", "ethereum", "base", "berachain", "bsc"],
      ethereum: ["solana", "ethereum", "base", "berachain", "bsc"],
      solana: ["base", "solana", "ethereum", "berachain", "bsc"],
      base: ["solana", "ethereum", "base", "berachain", "bsc"],
      bsc: ["solana", "ethereum", "base", "berachain", "bsc"],
    },
    minimumEntryUSD: MIN_ENTRY_USD - MIN_ENTRY_USD_BUFFER,
    minimumGasBufferUSD,
    minimumCrossChainGasBufferUSD,
    minimumGasBufferAmount,
    minimumCrossChainGasBufferAmount,
    from: {
      isNative: true,
    },
    to: {
      isNative: false,
    },
    ctaColor: "green",
    approvalSpendingAmount,
  },
  SELL: {
    modeSwitch: ["SELL", "BUY"],
    supportedNativeChains: {
      berachain: ["solana", "ethereum", "base", "berachain", "bsc"],
      ethereum: ["solana", "ethereum", "base", "berachain", "bsc"],
      solana: ["base", "solana", "ethereum", "berachain", "bsc"],
      base: ["solana", "ethereum", "base", "berachain", "bsc"],
      bsc: ["solana", "ethereum", "base", "berachain", "bsc"],
    },
    minimumGasBufferUSD,
    minimumCrossChainGasBufferUSD,
    minimumGasBufferAmount,
    minimumCrossChainGasBufferAmount,
    from: {
      isNative: false,
    },
    to: {
      isNative: true,
    },
    ctaColor: "red",
    approvalSpendingAmount,
  },
  SWAP: {
    modeSwitch: null,
    supportedNativeChains: {
      berachain: ["solana", "ethereum", "base", "berachain", "bsc"],
      ethereum: ["solana", "ethereum", "base", "berachain", "bsc"],
      solana: ["base", "solana", "ethereum", "berachain", "bsc"],
      base: ["solana", "ethereum", "base", "berachain", "bsc"],
      bsc: ["solana", "ethereum", "base", "berachain", "bsc"],
    },
    minimumEntryUSD: MIN_ENTRY_USD - MIN_ENTRY_USD_BUFFER,
    minimumGasBufferUSD,
    minimumCrossChainGasBufferUSD,
    minimumGasBufferAmount,
    minimumCrossChainGasBufferAmount,
    from: {
      isNative: true,
    },
    to: {
      isBlockedSameChain: true,
      isNative: true,
    },
    ctaColor: "primary",
    approvalSpendingAmount,
  },
  UNWRAP: {
    modeSwitch: null,
    supportedNativeChains: {
      ethereum: ["solana", "ethereum", "base"],
      solana: ["solana", "ethereum", "base"],
      base: ["solana", "ethereum", "base"],
    },
    from: {
      isWrapped: true,
      isNative: true,
    },
    to: {
      isNative: false,
    },
    ctaColor: "primary",
    approvalSpendingAmount,
  },
};

export const getChainsByMode = (
  mode: Mode,
  nativeChain: Chain,
  targetChain: Chain,
): { from: Chain; to: Chain } => {
  return {
    from: TRADING_MODE_CONFIG_MAP[mode].from.isNative ? nativeChain : targetChain,
    to: TRADING_MODE_CONFIG_MAP[mode].to.isNative ? nativeChain : targetChain,
  };
};
